export const MAX_FILE_BYTES = 15 * 1024 * 1024;

export const isIntlFormat = (bankFormat) => bankFormat === 'BBKIntl';

const BBK_COLUMNS = [
  { field: 'property', headerName: 'Property Name', width: 220 },
  { field: 'unitNo', headerName: 'Unit No', width: 110 },
  { field: 'refNo', headerName: 'Ref No', width: 140 },
  { field: 'systemCreditAccount', headerName: 'System Credit Account', width: 200 },
  { field: 'systemBeneficiaryName', headerName: 'System Beneficiary Name', width: 240 },
  { field: 'systemAmount', headerName: 'Budgeted Amount', width: 150, isAmount: true },
  { field: 'bankCreditAccount', headerName: 'Bank Credit Account', width: 200 },
  { field: 'bankBeneficiaryName', headerName: 'Bank Beneficiary Name', width: 240 },
  { field: 'bankAmount', headerName: 'Bank Transfer Amount', width: 170, isAmount: true },
];

const INTL_COLUMNS = [
  { field: 'refNo', headerName: 'Rec No', width: 120 },
  { field: 'property', headerName: 'System Property', width: 220 },
  { field: 'systemCreditAccount', headerName: 'System Credit Account IBAN', width: 240 },
  { field: 'systemBeneficiaryName', headerName: 'System Beneficiary Name', width: 240 },
  { field: 'bankCreditAccount', headerName: 'Bank Credit Account IBAN', width: 240 },
  { field: 'bankBeneficiaryName', headerName: 'Bank Beneficiary Name', width: 240 },
  { field: 'bankSwiftCode', headerName: 'Bank Swift Code', width: 150 },
  { field: 'bankProperty', headerName: 'Bank Property', width: 220 },
  { field: 'matchStatus', headerName: 'Match Status', width: 150, isStatus: true },
];

/** BBK and Khaleeji share a layout; BBKIntl has no amount columns at all. */
export const columnsFor = (bankFormat) => (isIntlFormat(bankFormat) ? INTL_COLUMNS : BBK_COLUMNS).map((c) => ({ ...c }));

/**
 * Field names to highlight for a row. The flags come from the server; nothing is compared here.
 * Amount differences stay off unless the toggle is on - the old screen computed them but never showed them.
 */
export const cellFlags = (row, highlightAmount = false) => {
  const fields = [];
  if (!row) return fields;
  if (row.isCreditAccountMismatch) fields.push('systemCreditAccount', 'bankCreditAccount');
  if (row.isBeneficiaryNameMismatch) fields.push('systemBeneficiaryName', 'bankBeneficiaryName');
  if (highlightAmount && row.isAmountMismatch) fields.push('systemAmount', 'bankAmount');
  return fields;
};

/** Text for the export's "Mismatch" column so the highlighting survives in the file. */
export const mismatchLabel = (row) => {
  if (!row) return '';
  if (row.isUnitNotFound) return 'Not matched';
  if (row.isCreditAccountMismatch && row.isBeneficiaryNameMismatch) return 'Both';
  if (row.isCreditAccountMismatch) return 'Credit Account';
  if (row.isBeneficiaryNameMismatch) return 'Beneficiary Name';
  return '';
};

const norm = (v) => String(v ?? '').toLowerCase();

export const filterRows = (rows, { search = '', mismatchesOnly = false } = {}) => {
  const term = norm(search).trim();
  return (rows || []).filter((row) => {
    if (mismatchesOnly && !row.hasMismatch) return false;
    if (!term) return true;
    return [row.refNo, row.property, row.unitNo, row.systemBeneficiaryName, row.bankBeneficiaryName].some((v) => norm(v).includes(term));
  });
};

/** Returns an error message, or null when the file can be uploaded. Wording matches the server. */
export const acceptFile = (file) => {
  if (!file) return 'Select a file to upload.';
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.xls')) return 'This is an old .xls file. Open it in Excel and save it as .xlsx, then upload again.';
  if (!(name.endsWith('.xlsx') || name.endsWith('.xlsm'))) return 'Only .xlsx or .xlsm files can be uploaded.';
  if (file.size > MAX_FILE_BYTES) return 'The file is larger than 15 MB.';
  return null;
};

/** 3 decimals; blank (not 0.000) for null / undefined / empty. */
export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

export const isZeroDifference = (difference) => Number(difference ?? 0).toFixed(3) === '0.000';

const pad = (n) => String(n).padStart(2, '0');

/** BeneficiaryValidation_{bankFormat}_{yyyyMMdd_HHmm} */
export const exportFileName = (bankFormat, date = new Date()) =>
  `BeneficiaryValidation_${bankFormat}_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`;

/** Extracts the user-facing message from a failed axios call. */
export const errorMessage = (error) =>
  error?.response?.data?.message || (error?.code === 'ECONNABORTED' ? 'The request timed out. Try again.' : 'Something went wrong. Please try again.');

export const isBusyMessage = (message) => /another beneficiary validation is running/i.test(message || '');
export const isWrongFormatMessage = (message) => /none of the column headings/i.test(message || '');
